import { supabaseClient } from './supabase';
import { logger } from './logger';
import type { EventItem, Place } from '../types/shared/event';
import { HOTEL_ID } from './supabase/configClient';

class EventClient {
  private static instance: EventClient;

  private constructor() {
    console.log('🔥🔥🔥 EventClient constructor called 🔥🔥🔥');
  }

  public static getInstance(): EventClient {
    if (!EventClient.instance) {
      EventClient.instance = new EventClient();
      console.log('🔥🔥🔥 EventClient instance created 🔥🔥🔥');
    }
    return EventClient.instance;
  }

  public async getEvents(sectionId: string, startDate?: string, endDate?: string): Promise<EventItem[]> {
    try {
      console.log('🔥🔥🔥 EventClient.getEvents CALLED 🔥🔥🔥', { sectionId, startDate, endDate });
      
      logger.debug('Loading event items', {
        category: 'API',
        data: { 
          hotel_id: HOTEL_ID,
          section_id: sectionId,
          startDate,
          endDate
        }
      });

      // Vérifier les paramètres requis
      if (!sectionId) {
        throw new Error('Section ID is required');
      }

      // Construire les paramètres pour la fonction RPC
      const params: any = {
        p_section_id: sectionId,
        p_hotel_id: HOTEL_ID
      };

      // Ajouter les dates si elles sont fournies
      if (startDate) {
        params.p_date_start = startDate;
      }
      if (endDate) {
        params.p_date_end = endDate;
      }

      console.log('🔥🔥🔥 Calling RPC get_event_section_data with params:', JSON.stringify(params, null, 2));

      // Appeler la fonction RPC pour obtenir les événements
      const { data, error } = await supabaseClient.rpc('get_event_section_data', params);

      if (error) {
        console.error('🔥🔥🔥 RPC ERROR:', error);
        throw error;
      }

      console.log(`🔥🔥🔥 RPC SUCCESS! Got ${data?.length || 0} events`);
      if (data && data.length > 0) {
        console.log('🔥🔥🔥 First event sample:', JSON.stringify(data[0], null, 2).substring(0, 500) + '...');
      }

      // Charger les traductions pour les descriptions et les offres
      const [descriptionsResponse, offersResponse, showsResponse] = await Promise.all([
        supabaseClient
          .from('translations_section')
          .select('*')
          .eq('hotel_id', HOTEL_ID)
          .eq('section_id', sectionId)
          .eq('content_type', 'description'),
          
        supabaseClient
          .from('translations_section')
          .select('*')
          .eq('hotel_id', HOTEL_ID)
          .eq('section_id', sectionId)
          .eq('content_type', 'offer'),
          
        supabaseClient
          .from('event_shows')
          .select('id, offer, price_info')
          .eq('hotel_id', HOTEL_ID)
          .eq('section_id', sectionId)
      ]);

      if (descriptionsResponse.error) {
        throw descriptionsResponse.error;
      }

      if (showsResponse.error) {
        throw showsResponse.error;
      }

      // Créer un map des traductions de descriptions par item_id ou idshow
      const descriptionsMap = new Map();
      descriptionsResponse.data.forEach(translation => {
        const translations = {
          fr: translation.fr,
          en: translation.en,
          es: translation.es,
          it: translation.it,
          pt: translation.pt,
          de: translation.de,
          nl: translation.nl,
          ja: translation.ja,
          zh: translation.zh,
          ru: translation.ru
        };

        // Utiliser item_id ou idshow comme clé
        if (translation.item_id) {
          descriptionsMap.set(translation.item_id, translations);
        } else if (translation.idshow) {
          descriptionsMap.set(translation.idshow, translations);
        }

        logger.debug('Found description translations for event', {
          category: 'Translation',
          data: {
            item_id: translation.item_id,
            idshow: translation.idshow,
            translations: Object.entries(translations)
              .filter(([_, value]) => value)
              .reduce((acc, [key, value]) => ({
                ...acc,
                [key]: value.substring(0, 100) + (value.length > 100 ? '...' : '')
              }), {}),
            availableLanguages: Object.entries(translations)
              .filter(([_, value]) => value)
              .map(([key]) => key),
            section_id: sectionId,
            content_type: 'description'
          }
        });
      });
      
      // Créer un map des traductions d'offres par item_id ou idshow
      const offersMap = new Map();
      if (offersResponse.data) {
        offersResponse.data.forEach(translation => {
          const translations = {
            fr: translation.fr,
            en: translation.en,
            es: translation.es,
            it: translation.it,
            pt: translation.pt,
            de: translation.de,
            nl: translation.nl,
            ja: translation.ja,
            zh: translation.zh,
            ru: translation.ru
          };

          // Utiliser item_id ou idshow comme clé
          if (translation.item_id) {
            offersMap.set(translation.item_id, translations);
          } else if (translation.idshow) {
            offersMap.set(translation.idshow, translations);
          }

          logger.debug('Found offer translations for event', {
            category: 'Translation',
            data: {
              item_id: translation.item_id,
              idshow: translation.idshow,
              translations: Object.entries(translations)
                .filter(([_, value]) => value)
                .reduce((acc, [key, value]) => ({
                  ...acc,
                  [key]: value.substring(0, 100) + (value.length > 100 ? '...' : '')
                }), {}),
              availableLanguages: Object.entries(translations)
                .filter(([_, value]) => value)
                .map(([key]) => key),
              section_id: sectionId,
              content_type: 'offer'
            }
          });
        });
      }

      // Créer un map des offres par show_id
      const showsMap = new Map();
      showsResponse.data.forEach(show => {
        showsMap.set(show.id, {
          offer: show.offer,
          price_info: show.price_info
        });
      });

      console.log('🔥🔥🔥 Shows data loaded:', {
        showCount: showsResponse.data.length,
        showsWithOffers: showsResponse.data.filter(s => s.offer).length
      });

      // Log the raw response data
      logger.debug('Raw RPC response data:', {
        category: 'API',
        data: {
          count: data.length,
          firstEvent: data.length > 0 ? {
            id: data[0].id,
            name: data[0].name,
            date: data[0].date,
            place: data[0].place,
            idshow: data[0].idshow,
            show_id: data[0].show_id
          } : null
        }
      });

      // Transformer les données en EventItems
      const events = data.map(record => {
        // Récupérer les traductions pour cet événement
        const descriptions = descriptionsMap.get(record.id) || descriptionsMap.get(record.idshow || record.show_id) || {};
        const offers = offersMap.get(record.id) || offersMap.get(record.idshow || record.show_id) || {};
        
        // Récupérer l'offre depuis la table event_shows
        const showData = showsMap.get(record.idshow || record.show_id);
        const offer = showData?.offer || record.offer || '';

        logger.debug('Processing event with translations', {
          category: 'Translation',
          data: {
            item_id: record.id,
            idshow: record.idshow || record.show_id,
            item_name: record.name,
            description: record.description?.substring(0, 100) + (record.description?.length > 100 ? '...' : ''),
            offer: offer ? (offer.substring(0, 100) + (offer.length > 100 ? '...' : '')) : 'missing',
            has_descriptions: Object.keys(descriptions).length > 0,
            has_offers: Object.keys(offers).length > 0,
            descriptions: Object.entries(descriptions)
              .filter(([_, value]) => value)
              .reduce((acc, [key, value]) => ({
                ...acc,
                [key]: value.substring(0, 100) + (value.length > 100 ? '...' : '')
              }), {}),
            offers: Object.entries(offers)
              .filter(([_, value]) => value)
              .reduce((acc, [key, value]) => ({
                ...acc,
                [key]: value.substring(0, 100) + (value.length > 100 ? '...' : '')
              }), {}),
            section_id: sectionId
          }
        });

        return {
          id: record.id,
          name: record.name,
          descriptions: descriptions,
          description: record.description,
          offers: offers,
          offer: offer,
          fulladress: record.fulladress || '',
          telephone: record.telephone || '',
          url: record.url || '',
          category: record.category || 'Autre',
          type: record.type || '',
          image: record.image_metadata?.url || '',
          latitude: parseFloat(record.latitude) || 0,
          longitude: parseFloat(record.longitude) || 0,
          distance: parseFloat(record.distance) || 0,
          timewalk: record.timewalk?.toString() || '',
          timedrive: record.timedrive?.toString() || '',
          timebike: record.timebike?.toString() || '',
          booking: record.booking || '',
          price: record.price || showData?.price_info || '',
          artiste: record.artiste || '',
          place: record.place || '',
          date: record.date || '',
          hour: (record.hour || '00:00').substring(0, 5),
          idShow: record.idshow || record.show_id,
          idshow: record.idshow || record.show_id,
          show_id: record.show_id || record.idshow,
          source: 'event'
        };
      });

      logger.debug('Events loaded successfully', {
        category: 'API',
        data: { 
          eventCount: events.length,
          eventsWithOffers: events.filter(e => e.offer).length,
          sectionId,
          startDate,
          endDate
        }
      });

      return events;

    } catch (error) {
      console.error('🔥🔥🔥 ERROR in EventClient.getEvents:', error);
      logger.error('Error fetching events', {
        category: 'API',
        data: { error, sectionId, hotelId: HOTEL_ID }
      });
      throw error;
    }
  }

  public getPlaces(events: EventItem[]): Place[] {
    try {
      console.log('🔥🔥🔥 EventClient.getPlaces CALLED with', events.length, 'events');
      
      const placeMap = new Map<string, Place>();

      events.forEach(event => {
        if (!placeMap.has(event.place)) {
          placeMap.set(event.place, {
            name: event.place,
            latitude: event.latitude,
            longitude: event.longitude,
            distance: event.distance,
            fulladress: event.fulladress,
            itinerary: event.url,
            shows: []
          });
        }

        const place = placeMap.get(event.place)!;
        let show = place.shows.find(s => s.idShow === event.idShow);

        if (!show) {
          show = {
            id: event.id,
            idShow: event.idShow || '',
            name: event.name,
            image: event.image,
            type: event.type,
            events: []
          };
          place.shows.push(show);
        }

        show.events.push(event);
      });

      // Sort shows by date/time of first event
      const places = Array.from(placeMap.values()).map(place => ({
        ...place,
        shows: place.shows.sort((a, b) => {
          const aEvent = a.events[0];
          const bEvent = b.events[0];
          const dateCompare = aEvent.date.localeCompare(bEvent.date);
          return dateCompare !== 0 ? dateCompare : aEvent.hour.localeCompare(bEvent.hour);
        })
      }));

      console.log('🔥🔥🔥 EventClient.getPlaces returning', places.length, 'places');
      return places;

    } catch (error) {
      console.error('🔥🔥🔥 ERROR in EventClient.getPlaces:', error);
      logger.error('Error creating places', {
        category: 'API',
        data: { error }
      });
      return [];
    }
  }
}

const eventClient = EventClient.getInstance();

export { eventClient };